import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  IBlockStatusResponse,
  IPollResponse,
  IPollSubmissionResponse,
  IResendCodeResponse,
  ISuspiciousActivityState,
  IValidatePinResponse,
} from 'projects/lib-ui/src/shared/interfaces/poll-responses';
import { IPoll } from 'projects/lib-ui/src/shared/interfaces/poll';
import { IResponse } from 'projects/lib-ui/src/shared/interfaces/response';

@Injectable({
  providedIn: 'root',
})
export class PollService {
  private readonly URI_API = environment.URLAPI;
  private readonly URI_DEVICES = `${this.URI_API}Devices`;

  constructor(private readonly httpClient: HttpClient) {}

  getPollById(pollId: string): Observable<IResponse<IPoll>> {
    return this.httpClient
      .get<IResponse<IPoll>>(`${this.URI_API}Poll/${pollId}`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching poll by ID:', error);
          return of({ data: null, success: false, message: error.message });
        })
      );
  }

  sendPollResponse(
    pollResponse: IPollResponse
  ): Observable<IResponse<IPollSubmissionResponse>> {
    return this.httpClient
      .post<IResponse<IPollSubmissionResponse>>(
        `${this.URI_API}PollSubmission`,
        pollResponse
      )
      .pipe(
        map((response) => ({
          ...response,
          data: this.normalizeSubmissionResponse(response.data),
        }))
      );
  }

  validatePin(
    deviceId: string,
    pin: string
  ): Observable<IResponse<IValidatePinResponse>> {
    return this.httpClient.post<IResponse<IValidatePinResponse>>(
      `${this.URI_DEVICES}/${deviceId}/validate-pin`,
      { pin }
    );
  }

  resendCode(deviceId: string): Observable<IResponse<IResendCodeResponse>> {
    return this.httpClient.post<IResponse<IResendCodeResponse>>(
      `${this.URI_DEVICES}/${deviceId}/resend-code`,
      {}
    );
  }

  getBlockStatus(deviceId: string): Observable<IResponse<IBlockStatusResponse>> {
    return this.httpClient
      .get<IResponse<IBlockStatusResponse>>(
        `${this.URI_DEVICES}/${deviceId}/block-status`
      )
      .pipe(
        map((response) => ({
          ...response,
          data: {
            blockState: this.normalizeSuspiciousActivity(
              response.data?.blockState
            ),
          },
        }))
      );
  }

  private normalizeSubmissionResponse(
    data: IPollSubmissionResponse | null
  ): IPollSubmissionResponse | null {
    if (!data) {
      return data;
    }

    return {
      ...data,
      suspiciousActivity: this.normalizeSuspiciousActivity(data.suspiciousActivity),
    };
  }

  private normalizeSuspiciousActivity(
    suspiciousActivity: ISuspiciousActivityState | null | undefined
  ): ISuspiciousActivityState | null {
    if (!suspiciousActivity) {
      return null;
    }

    const validLevel = [1, 2, 3].indexOf(suspiciousActivity.alertLevel as number) >= 0
      ? suspiciousActivity.alertLevel
      : null;

    const parsedRemainingSeconds = Number(suspiciousActivity.remainingSeconds);
    const remainingSeconds = isFinite(parsedRemainingSeconds)
      ? Math.max(0, Math.floor(parsedRemainingSeconds))
      : 0;

    return {
      alertLevel: validLevel,
      remainingSeconds,
    };
  }
}
